import React, { useMemo, useState } from 'react';

import { Box, Container, IconButton, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';

import { ReactComponent as MoreIcon } from 'assets/icons/more.svg';
import AssetRelationsTabs from 'components/AssetRelationsTabs/AssetRelationsTabs';
import ColoredButton from 'components/ColoredButton';
import ContextMenu from 'components/ContextMenu/ContextMenu';
import Loader from 'components/Loader';
import ManagePageHeader from 'components/ManagePageHeader/ManagePageHeader';
import TextButton from 'components/TextButton/TextButton';
import MUTATION_KEYS from 'config/api/MUTATION_KEYS';
import organizationYearPlannerActivityTemplatesApi from 'config/api/organizationYearPlannerActivityTemplates/organizationYearPlannerActivityTemplates';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import {
  OrganizationYearPlannerActivityTemplate,
  OrganizationYearPlannerActivityTemplateInput,
} from 'config/api/yearPlannerActivities/yearPlannerActivities.types';
import assets_messages from 'messages/assets_messages';
import general_messages from 'messages/general_messages';
import { useConfirmationModalContext } from 'reactContext/ConfirmationModalContext/ConfirmationModalContext';
import { useHelpDialogContext } from 'reactContext/HelpDialogContext/HelpDialogContext';

const emptyForm: OrganizationYearPlannerActivityTemplateInput = {
  name: '',
  description: '',
};

const DESCRIPTION_PREVIEW_LENGTH = 200;

const getDescriptionPreview = (description: string) =>
  description.length > DESCRIPTION_PREVIEW_LENGTH ? `${description.slice(0, DESCRIPTION_PREVIEW_LENGTH)}...` : description;

const topAlignedCellStyle = { verticalAlign: 'top' };

const OrganizationYearPlannerActivityTemplatesPage: React.FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  // @ts-ignore
  const { showConfirmationModal } = useConfirmationModalContext();
  const { showHelpDialog } = useHelpDialogContext();
  const [search, setSearch] = useState('');
  const [editingTemplate, setEditingTemplate] = useState<OrganizationYearPlannerActivityTemplate | null>(null);
  const [formData, setFormData] = useState<OrganizationYearPlannerActivityTemplateInput>(emptyForm);

  const templatesQuery = useQuery(
    QUERY_KEYS.ORGANIZATION_YEAR_PLANNER_ACTIVITY_TEMPLATES,
    organizationYearPlannerActivityTemplatesApi.getAll,
  );

  const resetForm = () => {
    setEditingTemplate(null);
    setFormData(emptyForm);
  };

  const createMutation = useMutation(
    MUTATION_KEYS.ORGANIZATION_YEAR_PLANNER_TEMPLATE_CREATE,
    organizationYearPlannerActivityTemplatesApi.create,
    {
      onSuccess: () => {
        enqueueSnackbar(t(general_messages.data_saved), { variant: 'success' });
        resetForm();
        templatesQuery.refetch();
      },
    },
  );

  const updateMutation = useMutation(
    MUTATION_KEYS.ORGANIZATION_YEAR_PLANNER_TEMPLATE_UPDATE,
    organizationYearPlannerActivityTemplatesApi.update,
    {
      onSuccess: () => {
        enqueueSnackbar(t(general_messages.data_saved), { variant: 'success' });
        resetForm();
        templatesQuery.refetch();
      },
    },
  );

  const deleteMutation = useMutation(
    MUTATION_KEYS.ORGANIZATION_YEAR_PLANNER_TEMPLATE_DELETE,
    organizationYearPlannerActivityTemplatesApi.remove,
    {
      onSuccess: () => {
        enqueueSnackbar(t(general_messages.data_saved), { variant: 'success' });
        templatesQuery.refetch();
      },
    },
  );

  const filteredTemplates = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return templatesQuery.data || [];
    return (templatesQuery.data || []).filter(({ name }) => name.toLowerCase().includes(normalizedSearch));
  }, [search, templatesQuery.data]);

  const isSaving = createMutation.isLoading || updateMutation.isLoading;

  const submitForm = (event: React.FormEvent) => {
    event.preventDefault();
    if (!formData.name.trim()) return;
    const payload = {
      name: formData.name.trim(),
      description: formData.description,
    };
    if (editingTemplate) {
      updateMutation.mutate({ id: editingTemplate.id, payload });
      return;
    }
    createMutation.mutate(payload);
  };

  const startEdit = (template: OrganizationYearPlannerActivityTemplate) => {
    setEditingTemplate(template);
    setFormData({ name: template.name, description: template.description });
  };

  const removeTemplate = async (template: OrganizationYearPlannerActivityTemplate) => {
    const shouldProceed = await showConfirmationModal({
      title: t(assets_messages.year_planner_templates.delete_title),
      body: t(assets_messages.year_planner_templates.delete_body, { templateName: template.name }),
    });
    if (shouldProceed) {
      deleteMutation.mutate(template.id);
    }
  };

  const openHelpDialog = () =>
    showHelpDialog({
      title: t(assets_messages.year_planner_templates.help_title),
      body: t(assets_messages.year_planner_templates.help_body),
    });

  return (
    <Container>
      <ManagePageHeader
        rightAdornment={<TextButton onClick={openHelpDialog}>{t(assets_messages.year_planner_templates.help_link)}</TextButton>}
      >
        {t(assets_messages.page_header)}
      </ManagePageHeader>
      <AssetRelationsTabs />
      <Box component='section' my={3}>
        <Typography variant='h3'>{t(assets_messages.year_planner_templates.title)}</Typography>
        <Box component='form' display='grid' gridGap={16} mt={2} onSubmit={submitForm}>
          <TextField
            label={t(assets_messages.year_planner_templates.name)}
            onChange={event => setFormData(current => ({ ...current, name: event.target.value }))}
            required
            value={formData.name}
            variant='outlined'
          />
          <TextField
            inputProps={{ style: { resize: 'vertical' } }}
            label={t(assets_messages.year_planner_templates.description)}
            minRows={4}
            multiline
            onChange={event => setFormData(current => ({ ...current, description: event.target.value }))}
            value={formData.description}
            variant='outlined'
          />
          <Box display='flex' gridGap={8}>
            <ColoredButton
              className={undefined}
              customColor='secondary'
              disabled={isSaving}
              icon={false}
              showLoader={false}
              textLike={false}
              type='submit'
              variant='outlined'
            >
              {editingTemplate ? t(general_messages.save) : t(assets_messages.year_planner_templates.create)}
            </ColoredButton>
            {editingTemplate && (
              <ColoredButton
                className={undefined}
                customColor='none'
                disabled={isSaving}
                icon={false}
                onClick={resetForm}
                showLoader={false}
                textLike={false}
                variant='outlined'
              >
                {t(general_messages.cancel)}
              </ColoredButton>
            )}
          </Box>
        </Box>
      </Box>
      <Box mb={2}>
        <TextField
          fullWidth
          label={t(assets_messages.year_planner_templates.search)}
          onChange={event => setSearch(event.target.value)}
          value={search}
          variant='outlined'
        />
      </Box>
      {templatesQuery.isLoading ? (
        <Loader inner />
      ) : (
        <Box maxWidth='100%' overflow='auto'>
          <Table style={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell style={topAlignedCellStyle}>{t(assets_messages.year_planner_templates.name)}</TableCell>
                <TableCell style={topAlignedCellStyle}>{t(assets_messages.year_planner_templates.description)}</TableCell>
                <TableCell style={topAlignedCellStyle}>{t(assets_messages.year_planner_templates.created)}</TableCell>
                <TableCell style={topAlignedCellStyle}>{t(assets_messages.year_planner_templates.last_updated)}</TableCell>
                <TableCell align='right' style={topAlignedCellStyle} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTemplates.length === 0 && (
                <TableRow>
                  <TableCell align='center' colSpan={5}>
                    {t(assets_messages.year_planner_templates.no_templates)}
                  </TableCell>
                </TableRow>
              )}
              {filteredTemplates.map(template => (
                <TableRow key={template.id}>
                  <TableCell style={topAlignedCellStyle}>{template.name}</TableCell>
                  <TableCell style={{ ...topAlignedCellStyle, whiteSpace: 'pre-line' }}>
                    {getDescriptionPreview(template.description)}
                  </TableCell>
                  <TableCell style={topAlignedCellStyle}>{template.createdAt.slice(0, 10)}</TableCell>
                  <TableCell style={topAlignedCellStyle}>{template.updatedAt.slice(0, 10)}</TableCell>
                  <TableCell align='right' style={topAlignedCellStyle}>
                    <ContextMenu
                      elements={[
                        { label: t(assets_messages.year_planner_templates.edit), onClick: () => startEdit(template) },
                        { label: t(assets_messages.year_planner_templates.delete), onClick: () => removeTemplate(template) },
                      ]}
                      id={`year-planner-template-${template.id}`}
                    >
                      <IconButton aria-label={t(assets_messages.year_planner_templates.actions)}>
                        <MoreIcon />
                      </IconButton>
                    </ContextMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Container>
  );
};

export default OrganizationYearPlannerActivityTemplatesPage;
